<script lang="ts">
import type { Component, PropType } from 'vue';
import type { GaiaChipTone } from './GaiaStatusChip.vue';

/** One choice in the segmented track. */
export interface GaiaSegmentedOption {
  label: string;
  value: string;
  /** Muted count after the label. */
  count?: string | number;
  /**
   * Marks the choice with the tone's dot — the signing list's status tabs,
   * where the tab and the rows it filters to carry the same colour. Adapt
   * backend enums through statusMaps.ts, as the chips do.
   */
  tone?: GaiaChipTone;
  /**
   * Glyph before the label — the Reviews mock's Board/Table switcher, where the
   * two views are told apart by their icon as much as their word. Pass a
   * component (an @vicons icon); it inherits `.item`'s colour, so it flips with
   * the active pill.
   */
  icon?: Component;
  /**
   * Rendered as the option button's `data-tour` — the guided demos click tabs,
   * and the wrapper owns the buttons, so a caller has no element of its own to
   * put the key on (same reason GaiaWizardFooter takes `backAttrs`).
   */
  tourKey?: string;
}
</script>

<script setup lang="ts">
import GaiaText from './GaiaText.vue';
import { gaiaChipDot, gaiaNeutral, gaiaShadow } from '../themes/tokens';

const props = defineProps({
  value: {
    type: String,
    required: true,
  },
  options: {
    type: Array as PropType<GaiaSegmentedOption[]>,
    required: true,
  },
  /** small = the 24px header switcher; medium = the 32px form control. */
  size: {
    type: String as PropType<'small' | 'medium'>,
    default: 'small',
  },
  /** Stretch the items to fill the track (the wizard's Status field). */
  block: {
    type: Boolean,
    default: false,
  },
  /**
   * Icon above a small label, tab-bar style — the Word task pane's bottom nav,
   * where four icon-and-word pills side by side overflow a narrow pane.
   */
  stacked: {
    type: Boolean,
    default: false,
  },
  /**
   * Locks the whole track — a choice fixed after creation (a template's save
   * mode while a save is in flight, a classification's single/multiple once it
   * exists). The active pill still reads, so what was chosen stays legible.
   */
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{ 'update:value': [value: string] }>();

/* The palette is the Tabs theme's segment section (gaia-*.json): the track is
   `colorSegment`, the active pill `tabColorSegment`, and the labels
   `tabTextColor*Segment` — so this switcher and an n-tabs `type="segment"` read
   as the same control. */
const track = gaiaNeutral.surfaceTint;
const pill = gaiaNeutral.surface;
const pillShadow = gaiaShadow.segment;
const idle = gaiaNeutral.textSoft;
const ink = gaiaNeutral.ink;
const rule = gaiaNeutral.borderDivider;
</script>

<template>
  <!-- Segmented switcher (Playbooks mock: Draft/Active, Repository/Upload, Per
       run/Grid). naive's n-radio-button group is a joined-border control and
       can't express the inset pill inside a padded track, so the chrome is
       local CSS over tokens. -->
  <div
    class="gaia-segmented"
    :class="[props.size, { block: props.block, stacked: props.stacked, disabled: props.disabled }]"
    role="tablist"
  >
    <button
      v-for="opt in props.options"
      :key="opt.value"
      type="button"
      role="tab"
      class="item"
      :class="{ active: opt.value === props.value }"
      :aria-selected="opt.value === props.value"
      :disabled="props.disabled"
      :data-tour="opt.tourKey"
      :title="opt.label"
      @click="emit('update:value', opt.value)"
    >
      <!-- The tone dot: same colour the rows' status chips use, so the tab and
           what it filters to read as one thing. -->
      <span
        v-if="opt.tone"
        class="dot"
        :style="{ background: gaiaChipDot[opt.tone] }"
        aria-hidden="true"
      />
      <!-- Colour stays on .item (it flips between idle and ink on .item.active,
           which no tone names); size and weight are props. -->
      <!-- Inherits .item's colour through n-icon's currentColor, so it flips
           with the active pill exactly as the label does. -->
      <n-icon v-if="opt.icon" :component="opt.icon" :size="props.stacked ? 16 : 14" />
      <!-- Truncates in a block track, where the items share the width and can
           be squeezed below their label. -->
      <gaia-text
        :size="props.stacked ? 'micro' : 'control'"
        :weight="opt.value === props.value ? 'bold' : 'medium'"
        :truncate="props.block"
        :style="{ maxWidth: '100%' }"
      >
        {{ opt.label }}
      </gaia-text>
      <gaia-text
        v-if="opt.count !== undefined"
        size="meta"
        weight="medium"
        tone="faint"
        :style="{ fontVariantNumeric: 'tabular-nums' }"
      >
        {{ opt.count }}
      </gaia-text>
    </button>
  </div>
</template>

<style scoped>
/* Stays CSS: `.gaia-segmented.block` overrides `display` and the items' layout,
   and an n-flex would write those inline where the modifier can't reach them. */
.gaia-segmented {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px;
  /* Holds the track to its options. `inline-flex` alone is not enough: as a
     child of an `n-flex vertical` the column stretches it to full width and
     the track becomes a long empty bar with three pills at its left. */
  width: fit-content;
  border-radius: 9px;
  background: v-bind(track);
}

.gaia-segmented.block {
  display: flex;
  width: 100%;
}

.item {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 24px;
  padding: 0 12px;
  border: none;
  border-radius: 7px;
  background: none;
  font-family: inherit;
  color: v-bind(idle);
  white-space: nowrap;
  cursor: pointer;
}

.medium .item {
  height: 32px;
}

.block .item {
  flex: 1 1 0;
  min-width: 0;
  justify-content: center;
}

.stacked .item {
  flex-direction: column;
  gap: 2px;
  height: auto;
  padding: 5px 4px;
  position: relative;
}

/* Hairlines between the tabs, dropped on either side of the active pill so
   they never cut into its edge. */
.stacked .item + .item::before {
  content: '';
  position: absolute;
  left: -2px;
  top: 25%;
  bottom: 25%;
  width: 1px;
  background: v-bind(rule);
}

.stacked .item.active::before,
.stacked .item.active + .item::before {
  display: none;
}

.item:hover {
  color: v-bind(ink);
}

/* Dims the track rather than the labels, so the active pill still reads as the
   choice that was made. `disabled` on the buttons already blocks the clicks. */
.gaia-segmented.disabled {
  opacity: 0.55;
}

.gaia-segmented.disabled .item {
  cursor: not-allowed;
}

.item.active {
  background: v-bind(pill);
  color: v-bind(ink);
  box-shadow: v-bind(pillShadow);
}

.dot {
  width: 6px;
  height: 6px;
  flex: none;
  border-radius: 50%;
}
</style>
